import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Input,
  Label,
  DialogClose,
} from '@leadsnavi/ui-kit';
import { X } from 'lucide-react';
import * as EditorTypeAll from '../tinymce.d';
import useMailEditorStore from '@/stores/mailEditorStore';

interface Props {
  editorInstance: EditorTypeAll.Editor;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface LinkData {
  url: string;
  text: string;
}

const LinkDialog = ({ editorInstance, open, onOpenChange }: Props) => {
  const [linkData, setLinkData] = useState<LinkData>({
    url: '',
    text: '',
  });
  // 使用 selector 确保正确订阅 editorMode
  const editorMode = useMailEditorStore(state => state.editorMode);
  
  // 调试：打印 editorMode 的值
  useEffect(() => {
    if (open) {
      console.log('[LinkDialog] editorMode:', editorMode, 'open:', open, 'shouldApplyOverlayStyle:', editorMode === 'dialog');
    }
  }, [open, editorMode]);
  
  // 获取当前选中的链接信息
  useEffect(() => {
    if (open && editorInstance) {
      const selection = editorInstance.selection;
      const selectedNode = selection.getNode();

      // 检查是否选中了链接
      const linkElement = editorInstance.dom.getParent(selectedNode, 'a');

      if (linkElement) {
        // 编辑现有链接
        setLinkData({
          url: linkElement.getAttribute('href') || '',
          text: linkElement.textContent || '',
        });
      } else {
        // 创建新链接
        const selectedText = selection.getContent({ format: 'text' });
        setLinkData({
          url: '',
          text: selectedText || '',
        });
      }
    }
  }, [open, editorInstance]);

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLinkData(prev => ({ ...prev, url: e.target.value }));
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLinkData(prev => ({ ...prev, text: e.target.value }));
  };

  // 检查并格式化URL，如果没有协议则添加http://前缀
  const formatUrl = (url: string): string => {
    const trimmedUrl = url.trim();
    if (!trimmedUrl) return trimmedUrl;

    // 检查是否已包含协议（http://, https://, ftp://, mailto:, tel:等）
    const protocolRegex = /^[a-zA-Z][a-zA-Z\d+\-.]*:/;
    if (protocolRegex.test(trimmedUrl)) {
      return trimmedUrl; // 已包含协议，保持原样
    }

    // 没有协议，添加http://前缀
    return `http://${trimmedUrl}`;
  };

  const handleConfirm = () => {
    if (!linkData.url.trim()) {
      return;
    }

    // 格式化URL
    const formattedUrl = formatUrl(linkData.url);

    const selection = editorInstance.selection;
    const selectedNode = selection.getNode();
    const linkElement = editorInstance.dom.getParent(selectedNode, 'a');

    if (linkElement) {
      // 更新现有链接
      editorInstance.dom.setAttrib(linkElement, 'href', formattedUrl);
      if (linkData.text.trim()) {
        linkElement.textContent = linkData.text;
      }
      // 手动触发 change 事件，确保编辑器检测到内容变化
      editorInstance.fire('change');
    } else {
      // 创建新链接
      const linkHtml = `<a href="${formattedUrl}">${linkData.text || formattedUrl}</a>`;
      editorInstance.insertContent(linkHtml);
    }

    // 插入后检查内容是否为空，如果不为空则清除 placeholder
    // 使用 setTimeout 确保内容已插入到 DOM 中，并且编辑器的事件处理完成
    setTimeout(() => {
      const body = editorInstance.getBody();
      if (body) {
        // 检查内容是否为空：textContent 可以正确获取文本内容
        const textContent = body.textContent || '';
        // 如果内容不为空，则清除 placeholder
        if (textContent.trim().length > 0) {
          editorInstance.fire('setPlaceHolder', { value: null });
        }
      }
    }, 0);

    // 关闭对话框并重置数据
    onOpenChange(false);
    setLinkData({ url: '', text: '' });
  };

  const handleCancel = () => {
    onOpenChange(false);
    setLinkData({ url: '', text: '' });
  };

  const isConfirmDisabled = !linkData.url.trim();
  
  // 判断是否应该应用蒙层样式：只有在 dialog 模式下才应用
  const shouldApplyOverlayStyle = open && editorMode === 'dialog';

  return (
    <>
      {/* 动态设置 z-index 和蒙层样式 */}
      {shouldApplyOverlayStyle ? (
        <style>
          {`
            /* 覆盖蒙层背景色 - 使用正确的选择器 data-slot="dialog-overlay" */
            [data-slot="dialog-overlay"] {
              background-color: rgba(255, 255, 255, 0.5) !important;
              background: rgba(255, 255, 255, 0.5) !important;
            }
            [data-slot="dialog-overlay"][data-state="open"] {
              z-index: 10000 !important;
              background-color: rgba(255, 255, 255, 0.5) !important;
              background: rgba(255, 255, 255, 0.5) !important;
            }
            /* 确保覆盖 Tailwind 的 bg-black/50 类 */
            [data-slot="dialog-overlay"].bg-black\\/50,
            [data-slot="dialog-overlay"][class*="bg-black"] {
              background-color: rgba(255, 255, 255, 0.5) !important;
              background: rgba(255, 255, 255, 0.5) !important;
            }
            /* 兼容旧的选择器 */
            [data-radix-dialog-overlay] {
              background-color: rgba(255, 255, 255, 0.5) !important;
              background: rgba(255, 255, 255, 0.5) !important;
            }
            [data-radix-dialog-content][data-state="open"] {
              z-index: 10001 !important;
            }
          `}
        </style>
      ) : null}
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[500px] gap-0 p-6 rounded-lg z-[10001]" showCloseButton={false}>
          <DialogHeader className="pb-4 text-left relative">
            <DialogTitle className="text-lg align-left font-semibold text-gray-900 pr-8">Insert/Edit Link</DialogTitle>
            <DialogClose>
              <Button
                variant="ghost"
                size="sm"
                className="absolute right-0 top-0 h-6 w-6 p-0 hover:bg-[#E5E5E5] rounded-full"
              >
                <X className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </Button>
            </DialogClose>
          </DialogHeader>

          <div className="space-y-5 px-2 gap-0">
            {/* Link Address */}
            <div className="space-y-2">
              <Label htmlFor="link-url" className="text-sm font-medium text-gray-700">
                Link Address
              </Label>
              <Input
                id="link-url"
                type="url"
                placeholder="https://www.gator.ai/"
                value={linkData.url}
                onChange={handleUrlChange}
                className="w-full h-10 px-3 border border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Display words */}
            <div className="space-y-2">
              <Label htmlFor="link-text" className="text-sm font-medium text-gray-700">
                Display Text
              </Label>
              <Input
                id="link-text"
                type="text"
                placeholder="Please enter the text you want to display"
                value={linkData.text}
                onChange={handleTextChange}
                className="w-full h-10 px-3 border border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <DialogFooter className="flex justify-end space-x-3 gap-2 pt-6">
            <DialogClose>
              <Button
                variant="outline"
                onClick={handleCancel}
                className="px-4 py-2 border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 rounded-sm"
              >
                Cancel
              </Button>
            </DialogClose>
            <Button
              onClick={handleConfirm}
              disabled={isConfirmDisabled}
              className="px-4 py-2 bg-black text-white hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-sm"
            >
              Insert
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LinkDialog;
